import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

import "@aws-amplify/ui-react/styles.css";

import {
  Box,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

import {
  Church,
  CountryCode,
  LeadPastor,
  State,
  USAddress,
  ValidationError,
} from "baptize-america-client";

import { useAlert } from "../../components/AlertProvider";
import { getChurch, updateChurch } from "../../utils/client";
import DebouncedButton from "../../components/DebounceButton";
import TopNavBar from "../../components/TopNavBar";

const styles = {
  titleBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    mb: 5,
  },
  innerBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    mt: 2,
    mb: 2,
  },
  itemContainer: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    mt: 2,
    mb: 2,
    flexGrow: 1,
  },
  saveButtonBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    mt: 5,
  },
  saveButton: {
    backgroundColor: "#00169E",
    color: "#FFF",
    justifyContent: "center",
    height: 50,
    width: 300,
    fontSize: 16,
  },
};

/**
 * TODO:
 ** add Baptism Events
 ** validate/compare fetched vs. updated church details before calling updateChurch
 **/
const EditChurch = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [churchId] = useOutletContext<(string | undefined)[]>();
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const [prevChurchInfo, setPrevChurchInfo] = useState<Church>({
    address: {
      streetAddress1: undefined,
      streetAddress2: undefined,
      city: undefined,
      state: undefined,
      postalCode: undefined,
      country: undefined,
    },
    churchId: undefined,
    instagramUrl: undefined,
    leadPastor: {
      firstName: undefined,
      lastName: undefined,
    },
    name: undefined,
    websiteUrl: undefined,
    reported: undefined,
  });
  const [newAddress, setNewAddress] = useState<USAddress>({
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: undefined,
    postalCode: "",
    country: CountryCode.US,
  });
  const [newWebsiteUrl, setNewWebsiteUrl] = useState<string | undefined>(
    undefined,
  );
  const [newInstagramUrl, setNewInstagramUrl] = useState<string | undefined>(
    undefined,
  );
  const [pastorFirstName, setPastorFirstName] =
    useState<LeadPastor["firstName"]>(undefined);
  const [pastorLastName, setPastorLastName] =
    useState<LeadPastor["lastName"]>(undefined);
  const [newChurchName, setNewChurchName] = useState<string | undefined>(
    undefined,
  );

  const [errors, setErrors] = useState<{
    churchName?: string;
    leadPastorFirstName?: string;
    leadPastorLastName?: string;
    managerFirstName?: string;
    managerLastName?: string;
    managerEmail?: string;
    confirmEmail?: string;
    streetAddress1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    website?: string;
    instagram?: string;
  }>({});

  useEffect(() => {
    if (churchId) {
      getChurch({ churchId: churchId }).then(
        (church) => {
          setPrevChurchInfo(church);
          setNewAddress(church.address!);
          setNewWebsiteUrl(church?.websiteUrl);
          setNewInstagramUrl(church?.instagramUrl);
          setPastorFirstName(church.leadPastor?.firstName);
          setPastorLastName(church.leadPastor?.lastName);
          setNewChurchName(church?.name);
        },
        () => {
          navigate(`/churches/${churchId}`, { replace: true });
          console.log("Church not found");
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateChurch = async () => {
    // validate new values to see if changed using prevChurchInfo:
    if (prevChurchInfo.churchId !== churchId) return;

    const validateInputs = () => {
      const newErrors: Record<string, string> = {};
      if (!newChurchName?.trim())
        newErrors.churchName = "Church name is required.";
      if (!pastorFirstName?.trim())
        newErrors.leadPastorFirstName = "First name is required.";
      if (!pastorLastName?.trim())
        newErrors.leadPastorLastName = "Last name is required.";
      if (!newAddress.streetAddress1?.trim())
        newErrors.streetAddress1 = "Address is required.";
      if (!newAddress.city?.trim()) newErrors.city = "City is required.";
      if (!newAddress.state?.trim()) newErrors.state = "State is required.";
      if (!newAddress.postalCode?.trim())
        newErrors.postalCode = "Zip Code is required.";
      if (!newWebsiteUrl?.trim()) newErrors.website = "Website is required.";
      if (
        newWebsiteUrl?.trim() &&
        !newWebsiteUrl?.trim().startsWith("https://")
      )
        newErrors.website = "Website URL should start with https://";
      if (
        newInstagramUrl?.trim() &&
        !(
          newInstagramUrl.trim().startsWith("https://www.instagram.com/") ||
          newInstagramUrl.trim().startsWith("https://instagram.com/")
        )
      )
        newErrors.instagram =
          "Instagram URL should start with https://www.instagram.com/ or https://instagram.com/";

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    if (!validateInputs()) return;

    try {
      await updateChurch({
        churchId: churchId,
        address: newAddress,
        instagramUrl: newInstagramUrl,
        leadPastor: {
          firstName: pastorFirstName,
          lastName: pastorLastName,
        },
        name: newChurchName,
        websiteUrl: newWebsiteUrl,
        reported: prevChurchInfo.reported,
      });
      setAlert({
        showAlert: true,
        alertMsg: "Church updated successfully!",
        alertType: "success",
      });
    } catch (err) {
      console.log(`Unexpected Error updating church - ${JSON.stringify(err)}`);
      setAlert({
        showAlert: true,
        alertMsg:
          (err as unknown as ValidationError).errorMessage ??
          "Error Updating Church",
        alertType: "error",
      });
    }
    navigate(`/churches/${churchId}`, { replace: true });
  };

  return (
    <Container maxWidth={isLargeScreen ? "md" : false}>
      <Box sx={styles.titleBox} maxWidth={theme.breakpoints.values.md}>
        <TopNavBar showBackArrow={true} />
      </Box>
      <Box sx={styles.innerBox} minWidth={theme.breakpoints.values.sm}>
        <Typography
          align={"left"}
          variant={"h5"}
          fontFamily={"AmericanGrotesk-Bold"}
        >
          {"Edit Church Details"}
        </Typography>
      </Box>
      <Divider orientation="horizontal" />
      <TextField
        required={true}
        id={"outlined-required"}
        label={"Church Name"}
        value={newChurchName ?? ""}
        error={!!errors.churchName}
        helperText={errors.churchName}
        onChange={(e) => setNewChurchName(e.target.value)}
        sx={[styles.itemContainer]}
      />
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", display: "flex" }}
      >
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Lead Pastor First Name"}
          value={pastorFirstName ?? ""}
          error={!!errors.leadPastorFirstName}
          helperText={errors.leadPastorFirstName}
          onChange={(e) => setPastorFirstName(e.target.value)}
          sx={[styles.itemContainer, { mr: 1 }]}
        />
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Lead Pastor Last Name"}
          value={pastorLastName ?? ""}
          error={!!errors.leadPastorLastName}
          helperText={errors.leadPastorLastName}
          onChange={(e) => setPastorLastName(e.target.value)}
          sx={[styles.itemContainer, { ml: 1 }]}
        />
      </Stack>
      <Stack
        direction="column"
        sx={{ justifyContent: "space-between", display: "flex" }}
      >
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Street Address"}
          error={!!errors.streetAddress1}
          helperText={errors.streetAddress1}
          value={newAddress?.streetAddress1 ?? ""}
          onChange={(e) =>
            setNewAddress({ ...newAddress, streetAddress1: e.target.value })
          }
          sx={styles.itemContainer}
        />
        <TextField
          required={false}
          id={"outlined-required"}
          label={"Unit/Suite/Other"}
          value={newAddress?.streetAddress2 ?? ""}
          onChange={(e) =>
            setNewAddress({ ...newAddress, streetAddress2: e.target.value })
          }
          sx={styles.itemContainer}
        />
      </Stack>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", display: "flex", flex: 1 }}
      >
        <TextField
          required={true}
          id={"outlined-required"}
          label={"City"}
          error={!!errors.city}
          helperText={errors.city}
          value={newAddress?.city ?? ""}
          onChange={(e) =>
            setNewAddress({ ...newAddress, city: e.target.value })
          }
          sx={[styles.itemContainer, { mr: 1, flex: 0.48 }]}
        />
        <TextField
          id="state-select"
          select
          required
          label="State"
          slotProps={{
            select: {
              native: true,
            },
          }}
          value={newAddress?.state}
          error={!!errors.state}
          helperText={errors.state}
          onChange={(e) =>
            setNewAddress({ ...newAddress, state: e.target.value as State })
          }
          sx={[styles.itemContainer, { mr: 1, flex: 0.24, minWidth: 75 }]}
        >
          {Object.keys(State).map((option, index) => (
            <option key={`Select_${option}_${index}`} value={option}>
              {option}
            </option>
          ))}
        </TextField>
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Zip Code"}
          error={!!errors.postalCode}
          helperText={errors.postalCode}
          value={newAddress?.postalCode ?? ""}
          onChange={(e) =>
            setNewAddress({ ...newAddress, postalCode: e.target.value })
          }
          sx={[styles.itemContainer, { flex: 0.28, minWidth: 80 }]}
        />
      </Stack>
      <Stack
        direction="column"
        sx={{ justifyContent: "space-between", display: "flex" }}
      >
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Website URL"}
          placeholder={"eg: https://baptize.org"}
          value={newWebsiteUrl ?? ""}
          error={!!errors.website}
          helperText={errors.website}
          onChange={(e) => setNewWebsiteUrl(e.target.value)}
          sx={styles.itemContainer}
        />
        <TextField
          id={"outlined"}
          label={"Instagram (optional)"}
          placeholder={"eg: https://instagram.com/baptizeamerica"}
          value={newInstagramUrl ?? ""}
          error={!!errors.instagram}
          helperText={errors.instagram}
          onChange={(e) => setNewInstagramUrl(e.target.value)}
          sx={styles.itemContainer}
        />
      </Stack>
      <Box sx={styles.saveButtonBox}>
        <DebouncedButton
          variant="outlined"
          sx={styles.saveButton}
          endIcon={<SaveIcon />}
          onClick={handleUpdateChurch}
        >
          {"Save"}
        </DebouncedButton>
      </Box>
    </Container>
  );
};

export default EditChurch;
